import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const ComparePage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [cameras, setCameras] = useState([])
  const [expiryTime, setExpiryTime] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState('')
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    // Get comparison ID from URL
    const comparisonId = searchParams.get('id')

    if (!comparisonId) {
      navigate('/database')
      return
    }

    // Load comparison from localStorage
    const storedComparison = localStorage.getItem(`comparison_${comparisonId}`)

    if (!storedComparison) {
      setExpired(true)
      return
    }

    const comparison = JSON.parse(storedComparison)

    // Check if expired
    const expiryDate = new Date(comparison.expiryTime)
    const now = new Date()

    if (now > expiryDate) {
      localStorage.removeItem(`comparison_${comparisonId}`)
      setExpired(true)
      return
    }

    setCameras(comparison.cameras)
    setExpiryTime(expiryDate)
  }, [searchParams, navigate])

  // Update countdown timer
  useEffect(() => {
    if (!expiryTime) return

    const updateTimer = () => {
      const now = new Date()
      const diff = expiryTime - now

      if (diff <= 0) {
        setExpired(true)
        const comparisonId = searchParams.get('id')
        localStorage.removeItem(`comparison_${comparisonId}`)
        return
      }

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`)
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [expiryTime, searchParams])

  const handleDownloadCSV = () => {
    if (cameras.length === 0) return

    // Create CSV content
    const headers = ['Manufacturer', 'Model', 'Megapixels', 'Sensors', 'Type', 'Product Link']
    const rows = cameras.map(camera => [
      camera.manufacturer,
      camera.model,
      camera.megapixels,
      camera.sensors,
      camera.type,
      camera.product_link || '',
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `camera_comparison_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleDelete = () => {
    const comparisonId = searchParams.get('id')
    localStorage.removeItem(`comparison_${comparisonId}`)
    navigate('/database')
  }

  if (expired) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="container mx-auto px-4 py-8 max-w-4xl"
      >
        <Alert severity="error" icon={<AccessTimeIcon />}>
          <Typography variant="h6" className="font-bold mb-2">
            Comparison Expired
          </Typography>
          <Typography variant="body2">
            This comparison has expired or doesn't exist. Comparisons are automatically deleted after 24 hours.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/database')}
            sx={{ mt: 2 }}
          >
            Back to Database
          </Button>
        </Alert>
      </motion.div>
    )
  }

  if (cameras.length === 0) {
    return (
      <Box className="flex items-center justify-center min-h-screen">
        <Typography className="text-gray-400">Loading comparison...</Typography>
      </Box>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-7xl"
    >
      {/* Header with Timer */}
      <Box className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <Box>
          <Box className="flex items-center gap-2 mb-2">
            <CompareArrowsIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Camera Comparison
            </h1>
          </Box>
          <Typography className="text-gray-400">
            Comparing {cameras.length} camera{cameras.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Timer */}
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Chip
            icon={<AccessTimeIcon />}
            label={`Expires in: ${timeRemaining}`}
            sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              color: '#f87171',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              fontSize: '0.9rem',
              padding: '20px 10px',
              '& .MuiChip-icon': {
                color: '#f87171',
              },
            }}
          />
        </motion.div>
      </Box>

      {/* Action Buttons */}
      <Box className="flex gap-2 mb-6 flex-wrap">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/database')}
          sx={{
            borderColor: 'rgba(59, 130, 246, 0.5)',
            color: '#60a5fa',
            '&:hover': {
              borderColor: '#60a5fa',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
            },
          }}
        >
          Back to Database
        </Button>

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCSV}
          sx={{
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669',
            },
          }}
        >
          Download CSV
        </Button>

        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          sx={{
            borderColor: 'rgba(239, 68, 68, 0.5)',
            color: '#f87171',
            '&:hover': {
              borderColor: '#f87171',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
            },
          }}
        >
          Delete Comparison
        </Button>
      </Box>

      {/* Comparison Table */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: 'rgba(10, 10, 10, 0.95)',
                    borderBottom: '2px solid rgba(59, 130, 246, 0.3)',
                    fontWeight: 'bold',
                    color: '#3b82f6',
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                  }}
                >
                  Specification
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell
                    key={index}
                    align="center"
                    sx={{
                      backgroundColor: 'rgba(10, 10, 10, 0.95)',
                      borderBottom: '2px solid rgba(59, 130, 246, 0.3)',
                      fontWeight: 'bold',
                      color: '#3b82f6',
                      minWidth: '200px',
                    }}
                  >
                    Camera {index + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Manufacturer */}
              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.05)' } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#9ca3af',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1,
                  }}
                >
                  Manufacturer
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell key={index} align="center">
                    <Chip
                      label={camera.manufacturer}
                      sx={{
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        color: '#60a5fa',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>

              {/* Model */}
              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.05)' } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#9ca3af',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1,
                  }}
                >
                  Model
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell key={index} align="center" sx={{ fontWeight: 'medium' }}>
                    {camera.model}
                  </TableCell>
                ))}
              </TableRow>

              {/* Megapixels */}
              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.05)' } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#9ca3af',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1,
                  }}
                >
                  Megapixels
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell key={index} align="center">
                    <Chip
                      label={`${camera.megapixels} MP`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(139, 92, 246, 0.2)',
                        color: '#a78bfa',
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>

              {/* Sensors */}
              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.05)' } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#9ca3af',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1,
                  }}
                >
                  Sensors
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell key={index} align="center">
                    {camera.sensors}
                  </TableCell>
                ))}
              </TableRow>

              {/* Type */}
              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.05)' } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#9ca3af',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1,
                  }}
                >
                  Type
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell key={index} align="center">
                    <Chip
                      label={camera.type}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        color: '#3b82f6',
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>

              {/* Product Link */}
              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.05)' } }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    color: '#9ca3af',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    zIndex: 1,
                  }}
                >
                  Product Link
                </TableCell>
                {cameras.map((camera, index) => (
                  <TableCell key={index} align="center">
                    {camera.product_link ? (
                      <Button
                        variant="outlined"
                        size="small"
                        href={camera.product_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          borderColor: 'rgba(59, 130, 246, 0.5)',
                          color: '#60a5fa',
                          '&:hover': {
                            borderColor: '#60a5fa',
                          },
                        }}
                      >
                        View
                      </Button>
                    ) : (
                      <Typography className="text-gray-500">N/A</Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </motion.div>

      {/* Download Button (Fixed Bottom Right) */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: 'spring' }}
        style={{
          position: 'fixed',
          bottom: '100px',
          right: '20px',
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={handleDownloadCSV}
          sx={{
            width: 60,
            height: 60,
            backgroundColor: '#10b981',
            color: 'white',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
            '&:hover': {
              backgroundColor: '#059669',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s',
          }}
        >
          <DownloadIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </motion.div>

      {/* Info Alert */}
      <Alert
        severity="info"
        sx={{
          mt: 4,
          backgroundColor: 'rgba(26, 26, 26, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          color: '#9ca3af',
          '& .MuiAlert-icon': {
            color: '#3b82f6',
          },
        }}
      >
        <Typography variant="body2">
          This comparison will be automatically deleted after 24 hours. Download the CSV file if you need to keep this comparison for longer.
        </Typography>
      </Alert>
    </motion.div>
  )
}

export default ComparePage
